import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { courseAPI, groupAPI, authAPI, categoryAPI, tagAPI, BASE_URL } from '../services/api';
import Select from 'react-select';

function CourseSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    code: '',
    tags: [],
    category: '',
    status: 'draft',
    startDate: '',
    endDate: '',
    thumbnail: '',
    thumbnailPublicId: '',
    settings: {
      allowSelfEnrollment: false,
      requireApproval: false,
      maxStudents: '',
      showProgressToStudents: true,
      allowDiscussion: false
    }
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Enrollment data
  const [enrollments, setEnrollments] = useState({ groups: [], students: [] });
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  
  // Categories and tags
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showEnrollGroupModal, setShowEnrollGroupModal] = useState(false);
  const [showEnrollStudentModal, setShowEnrollStudentModal] = useState(false);
  
  // Delete confirmation
  const [deleteConfirmCode, setDeleteConfirmCode] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Copy form
  const [copyForm, setCopyForm] = useState({
    newCode: '',
    newTitle: '',
    targetGroupIds: []
  });
  const [copying, setCopying] = useState(false);
  
  // Enrollment selections
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  
  // Unified search query for enrollments tab
  const [searchQuery, setSearchQuery] = useState('');
  
  const [activeTab, setActiveTab] = useState('general'); // general, enrollments

  // Set active tab from URL search params
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'enrollments') {
      setActiveTab('enrollments');
    }
  }, [searchParams]);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/courses');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchCourse();
    fetchEnrollments();
    fetchGroups();
    fetchStudents();
    fetchCategories();
    fetchTags();
  }, [id]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getCourse(id);
      const courseData = response.data.course;
      setCourse(courseData);
      
      setEditForm({
        title: courseData.title,
        description: courseData.description || '',
        code: courseData.code,
        tags: courseData.tags?.map(t => t._id) || [],
        category: courseData.category?._id || '',
        status: courseData.status,
        startDate: courseData.startDate ? courseData.startDate.split('T')[0] : '',
        endDate: courseData.endDate ? courseData.endDate.split('T')[0] : '',
        thumbnail: courseData.thumbnail || '',
        thumbnailPublicId: courseData.thumbnailPublicId || '',
        settings: courseData.settings || {
          allowSelfEnrollment: false,
          requireApproval: false,
          maxStudents: '',
          showProgressToStudents: true,
          allowDiscussion: false
        }
      });
      
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch course');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const response = await courseAPI.getEnrollments(id);
      console.log('Fetched enrollments:', response.data);
      console.log('Groups:', response.data.groups);
      console.log('Students:', response.data.students);
      
      // Ensure we always have arrays
      const enrollmentData = {
        groups: Array.isArray(response.data.groups) ? response.data.groups : [],
        students: Array.isArray(response.data.students) ? response.data.students : []
      };
      
      console.log('Setting enrollments with:', enrollmentData);
      setEnrollments(enrollmentData);
    } catch (err) {
      console.error('Error fetching enrollments:', err);
      // Set empty arrays on error
      setEnrollments({ groups: [], students: [] });
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupAPI.getMyGroups();
      setAvailableGroups(response.data.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await authAPI.getAllStudents(1, 1000); // Get all students for course enrollment
      setAvailableStudents(response.data.students || []);
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await tagAPI.getAllTags();
      setTags(response.data.tags || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await fetch(`${BASE_URL}/api/course/upload-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: uploadFormData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      setEditForm(prev => ({
        ...prev,
        thumbnail: data.image.url,
        thumbnailPublicId: data.image.publicId
      }));
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    try {
      const response = await courseAPI.updateCourse(id, editForm);
      setCourse(response.data.course);
      setSuccess('Course updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update course');
    }
  };

  const handleDeleteCourse = async () => {
    if (deleteConfirmCode.toUpperCase() !== course.code) {
      setError('Course code does not match. Please enter the correct code.');
      return;
    }
    
    try {
      setDeleting(true);
      await courseAPI.deleteCourse(id, deleteConfirmCode);
      navigate('/courses');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete course');
      setDeleting(false);
    }
  };

  const handleCopyCourse = async (e) => {
    e.preventDefault();
    try {
      setCopying(true);
      const response = await courseAPI.duplicateCourse(id, copyForm);
      setSuccess('Course copied successfully!');
      setShowCopyModal(false);
      setCopyForm({ newCode: '', newTitle: '', targetGroupIds: [] });
      setTimeout(() => {
        navigate(`/course-builder/${response.data.course._id}`);
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy course');
    } finally {
      setCopying(false);
    }
  };

  const handleEnrollGroup = async () => {
    if (!selectedGroupId) {
      setError('Please select a group');
      return;
    }
    
    try {
      const response = await courseAPI.enrollGroup(id, selectedGroupId);
      
      // Show detailed success message
      if (response.data.summary) {
        const { summary } = response.data;
        const message = `Group "${summary.groupName}" enrolled successfully! ` +
          `${summary.newEnrollments} new, ${summary.reactivatedEnrollments} reactivated, ` +
          `${summary.alreadyEnrolled} already enrolled.`;
        setSuccess(message);
      } else {
        setSuccess(response.data.message || 'Group enrolled successfully!');
      }
      
      // Show errors if any
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Some students could not be enrolled:', response.data.errors);
        const errorMessage = `Warning: ${response.data.errors.length} student(s) could not be enrolled.`;
        setTimeout(() => {
          setError(errorMessage);
          setTimeout(() => setError(''), 5000);
        }, 3000);
      }
      
      setShowEnrollGroupModal(false);
      setSelectedGroupId('');
      fetchEnrollments();
      fetchCourse();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to enroll group');
    }
  };

  const handleEnrollStudents = async () => {
    if (selectedStudentIds.length === 0) {
      setError('Please select at least one student');
      return;
    }
    
    try {
      await courseAPI.enrollStudents(id, selectedStudentIds);
      setSuccess('Students enrolled successfully!');
      setShowEnrollStudentModal(false);
      setSelectedStudentIds([]);
      fetchEnrollments();
      fetchCourse();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to enroll students');
    }
  };

  const handleUnenrollGroup = async (groupId) => {
    const group = enrollments.groups?.find(g => g._id === groupId);
    const groupName = group?.name || 'this group';
    const studentCount = group?.students?.length || 0;
    
    const confirmMessage = `Are you sure you want to unenroll "${groupName}"?\n\n` +
      `This will unenroll ${studentCount} student(s) who were enrolled via this group.\n` +
      `Students will lose access to this course.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const response = await courseAPI.unenrollGroup(id, groupId);
      const successMsg = response.data.studentsUnenrolled 
        ? `Group "${groupName}" unenrolled successfully! ${response.data.studentsUnenrolled} student(s) removed.`
        : `Group "${groupName}" unenrolled successfully!`;
      setSuccess(successMsg);
      fetchEnrollments();
      fetchCourse();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unenroll group');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleUnenrollStudent = async (studentId) => {
    const student = enrollments.students?.find(s => s._id === studentId);
    const studentName = student?.name || 'this student';
    const studentEmail = student?.email || '';
    const progress = student?.enrollment?.progress?.percentageComplete || 0;
    
    const confirmMessage = `Are you sure you want to unenroll ${studentName}?\n\n` +
      `Email: ${studentEmail}\n` +
      `Current Progress: ${progress}%\n\n` +
      `The student will lose access to this course.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await courseAPI.unenrollStudent(id, studentId);
      setSuccess(`${studentName} unenrolled successfully!`);
      fetchEnrollments();
      fetchCourse();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unenroll student');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { bg: '#fef3c7', color: '#92400e', text: '📝 Draft' },
      published: { bg: '#d1fae5', color: '#065f46', text: '✓ Published' },
      archived: { bg: '#e5e7eb', color: '#374151', text: '📦 Archived' }
    };
    const badge = badges[status] || badges.draft;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        background: badge.bg,
        color: badge.color
      }}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#e53e3e', fontSize: '18px' }}>Course not found</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '1200px', padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '24px', 
        background: '#ffffff', 
        padding: '20px', 
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
          <button 
            onClick={() => navigate('/courses')}
            style={{ 
              background: 'transparent', 
              border: 'none', 
              fontSize: '24px', 
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ←
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', color: '#1a202c' }}>
              Course Settings
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: '#4a5568' }}>
                {course.title}
              </h2>
              {getStatusBadge(course.status)}
              <span style={{ fontSize: '14px', color: '#718096' }}>
                Code: <strong>{course.code}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fee2e2',
          color: '#991b1b',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={{
          padding: '12px 16px',
          background: '#d1fae5',
          color: '#065f46',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        background: '#ffffff',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          onClick={() => navigate(`/course-builder/${id}`)}
          className="btn btn-primary"
          style={{ fontSize: '14px' }}
        >
          📚 Edit Content & Structure
        </button>
        <button
          onClick={() => setShowCopyModal(true)}
          className="btn btn-secondary"
          style={{ fontSize: '14px' }}
        >
          📋 Copy Course
        </button>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="btn"
          style={{ 
            fontSize: '14px',
            background: '#fee2e2',
            color: '#991b1b',
            border: '1px solid #fecaca'
          }}
        >
          🗑️ Delete Course
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '24px',
        background: '#ffffff',
        padding: '20px 20px 0 20px',
        borderRadius: '12px 12px 0 0',
        border: '1px solid #e2e8f0',
        borderBottom: 'none'
      }}>
        {['general', 'enrollments'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === tab ? '#3b82f6' : '#718096',
              fontWeight: activeTab === tab ? '600' : '400',
              cursor: 'pointer',
              fontSize: '14px',
              textTransform: 'capitalize',
              marginBottom: '-2px'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Search Bar - Shows for enrollments tab */}
      {activeTab === 'enrollments' && (
        <div style={{
          background: '#ffffff',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          marginBottom: '16px'
        }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search students by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 40px',
                fontSize: '14px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
            <span style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '16px',
              color: '#a0aec0'
            }}>🔍</span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '18px',
                  color: '#a0aec0',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#a0aec0'}
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}

      {/* General Tab */}
      {activeTab === 'general' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '0 0 12px 12px',
          padding: '24px',
          border: '1px solid #e2e8f0',
          borderTop: 'none',
          marginTop: '-24px'
        }}>
          <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#1a202c' }}>
            General Information
          </h3>
          
          <form onSubmit={handleUpdateCourse}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                required
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows="4"
                style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
              />
            </div>

            <div className="form-group">
              <label>Course Code * (Cannot be changed)</label>
              <input
                type="text"
                value={editForm.code}
                disabled
                style={{ 
                  background: '#f7fafc', 
                  color: '#718096', 
                  border: '1px solid #e2e8f0',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            <div className="form-group">
              <label>Course Thumbnail (600 x 400 recommended)</label>
              {editForm.thumbnail ? (
                <div style={{ 
                  background: '#f8fafc',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '2px dashed #cbd5e1'
                }}>
                  <div style={{ 
                    marginBottom: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#059669'
                  }}>
                    ✓ Current Thumbnail Preview
                  </div>
                  <div style={{ 
                    position: 'relative',
                    width: '100%',
                    maxWidth: '600px',
                    margin: '0 auto'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '400px',
                      background: '#1a202c',
                      borderRadius: '8px',
                      border: '2px solid #059669',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <img 
                        src={editForm.thumbnail} 
                        alt="Course thumbnail" 
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, thumbnail: '', thumbnailPublicId: '' })}
                        style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          background: '#ef4444',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          transition: 'all 0.2s',
                          zIndex: 10
                        }}
                        onMouseOver={(e) => e.target.style.background = '#dc2626'}
                        onMouseOut={(e) => e.target.style.background = '#ef4444'}
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{ 
                      width: '100%', 
                      padding: '10px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#1a202c'
                    }}
                  />
                  {uploadingImage && (
                    <p style={{ fontSize: '12px', color: '#667eea', marginTop: '8px' }}>
                      🔄 Uploading image...
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                    Recommended size: 600 x 400 pixels. Max file size: 5MB
                  </p>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className="form-group">
              <label>Category</label>
              <select
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
              >
                <option value="">-- Select Category --</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <Select
                isMulti
                menuPortalTarget={document.body}
                options={tags.map(tag => ({ value: tag._id, label: tag.name }))}
                value={tags.filter(tag => editForm.tags.includes(tag._id)).map(tag => ({ value: tag._id, label: tag.name }))}
                onChange={(selected) => setEditForm({ ...editForm, tags: selected ? selected.map(s => s.value) : [] })}
                styles={{
                  control: (base) => ({ ...base, background: '#ffffff', borderColor: '#e2e8f0' }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                  style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                />
              </div>

              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={editForm.endDate}
                  onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                  style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
              <h4 style={{ fontSize: '16px', marginBottom: '16px', color: '#1a202c' }}>
                Course Settings
              </h4>

              <div style={{ display: 'grid', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.settings.allowSelfEnrollment}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      settings: { ...editForm.settings, allowSelfEnrollment: e.target.checked }
                    })}
                  />
                  <span style={{ fontSize: '14px', color: '#1a202c' }}>Allow Self Enrollment</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.settings.requireApproval}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      settings: { ...editForm.settings, requireApproval: e.target.checked }
                    })}
                  />
                  <span style={{ fontSize: '14px', color: '#1a202c' }}>Require Approval for Enrollment</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.settings.showProgressToStudents}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      settings: { ...editForm.settings, showProgressToStudents: e.target.checked }
                    })}
                  />
                  <span style={{ fontSize: '14px', color: '#1a202c' }}>Show Progress to Students</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.settings.allowDiscussion}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      settings: { ...editForm.settings, allowDiscussion: e.target.checked }
                    })}
                  />
                  <span style={{ fontSize: '14px', color: '#1a202c' }}>Allow Discussion</span>
                </label>

                <div className="form-group">
                  <label>Max Students (optional)</label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.settings.maxStudents}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      settings: { ...editForm.settings, maxStudents: e.target.value }
                    })}
                    placeholder="Leave empty for unlimited"
                    style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="submit" className="btn btn-primary">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Enrollments Tab */}
      {activeTab === 'enrollments' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '0 0 12px 12px',
          padding: '24px',
          border: '1px solid #e2e8f0',
          borderTop: 'none',
          marginTop: '-24px'
        }}>
          {/* Enrollment Statistics Panel */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '32px',
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            color: '#ffffff'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                {enrollments.groups?.length || 0}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>👥 Enrolled Groups</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                {enrollments.students?.length || 0}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>👤 Total Students</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                {enrollments.students?.filter(s => s.enrollment?.enrollmentSource === 'group').length || 0}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Via Groups</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                {enrollments.students?.filter(s => s.enrollment?.enrollmentSource === 'individual').length || 0}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Individual</div>
            </div>
          </div>

          {/* Enrolled Groups */}
          <div style={{
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', margin: 0, color: '#1a202c' }}>
                Enrolled Groups ({enrollments.groups?.length || 0})
              </h3>
              <button
                onClick={() => setShowEnrollGroupModal(true)}
                className="btn btn-primary"
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                + Enroll Group
              </button>
            </div>

            <div style={{ display: 'grid', gap: '12px' }}>
              {enrollments.groups && enrollments.groups.length > 0 ? (
                enrollments.groups.map(group => (
                  <div 
                    key={group._id} 
                    style={{ 
                      padding: '16px', 
                      background: '#f7fafc', 
                      borderRadius: '12px', 
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#1a202c' }}>
                        👥 {group.name}
                      </h4>
                      <p style={{ margin: 0, fontSize: '12px', color: '#718096' }}>
                        {group.students?.length || 0} students
                      </p>
                    </div>
                    <button
                      onClick={() => handleUnenrollGroup(group._id)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        background: '#fee2e2',
                        color: '#991b1b',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#fecaca';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#fee2e2';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      🗑️ Remove Group
                    </button>
                  </div>
                ))
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  background: '#ffffff', 
                  borderRadius: '12px', 
                  border: '2px dashed #cbd5e0' 
                }}>
                  <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>👥</p>
                  <p style={{ color: '#718096', margin: 0 }}>No groups enrolled yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div style={{ 
            height: '1px', 
            background: '#e2e8f0', 
            margin: '24px 0' 
          }} />

          {/* Enrolled Students */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', margin: '0 0 4px 0', color: '#1a202c' }}>
                  All Enrolled Students ({enrollments.students?.length || 0})
                </h3>
                <p style={{ fontSize: '12px', color: '#718096', margin: 0 }}>
                  {(() => {
                    const groupStudents = enrollments.students?.filter(s => s.enrollment?.enrollmentSource === 'group').length || 0;
                    const individualStudents = enrollments.students?.filter(s => s.enrollment?.enrollmentSource === 'individual').length || 0;
                    return `${groupStudents} from groups • ${individualStudents} individually enrolled`;
                  })()}
                </p>
              </div>
              <button
                onClick={() => setShowEnrollStudentModal(true)}
                className="btn btn-primary"
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                + Enroll Students
              </button>
            </div>

            <div style={{ display: 'grid', gap: '12px' }}>
              {enrollments.students && enrollments.students.length > 0 ? (() => {
                const filteredStudents = enrollments.students.filter(student => {
                  if (!searchQuery) return true;
                  const searchLower = searchQuery.toLowerCase();
                  return student.name.toLowerCase().includes(searchLower) ||
                         student.email.toLowerCase().includes(searchLower);
                });

                return filteredStudents.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px', 
                    background: '#ffffff', 
                    borderRadius: '12px', 
                    border: '2px dashed #cbd5e0' 
                  }}>
                    <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>🔍</p>
                    <p style={{ color: '#718096', margin: 0 }}>
                      No students found matching "{searchQuery}"
                    </p>
                  </div>
                ) : (
                  filteredStudents.map(student => (
                  <div 
                    key={student._id} 
                    style={{ 
                      padding: '16px', 
                      background: '#f7fafc', 
                      borderRadius: '12px', 
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <h4 
                          onClick={() => navigate(`/course/${id}/student/${student._id}/progress`)}
                          style={{ 
                            margin: 0, 
                            fontSize: '15px', 
                            color: '#667eea',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontWeight: '600'
                          }}
                        >
                          👤 {student.name}
                        </h4>
                        {student.enrollment?.enrollmentSource === 'group' && (
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 8px',
                            background: '#e0e7ff',
                            color: '#4c51bf',
                            borderRadius: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            👥 Group
                          </span>
                        )}
                        {student.enrollment?.enrollmentSource === 'individual' && (
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 8px',
                            background: '#f0fdf4',
                            color: '#16a34a',
                            borderRadius: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            Individual
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '0 0 2px 0', fontSize: '12px', color: '#718096' }}>
                        {student.email}
                      </p>
                      {student.enrollment && (
                        <p style={{ margin: 0, fontSize: '11px', color: '#a0aec0' }}>
                          Enrolled: {new Date(student.enrollment.enrolledAt).toLocaleDateString()} • 
                          Progress: {student.enrollment.progress?.percentageComplete || 0}%
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleUnenrollStudent(student._id)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        background: '#fee2e2',
                        color: '#991b1b',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#fecaca';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#fee2e2';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      🗑️ Remove
                    </button>
                  </div>
                  ))
                );
              })() : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  background: '#ffffff', 
                  borderRadius: '12px', 
                  border: '2px dashed #cbd5e0' 
                }}>
                  <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>👤</p>
                  <p style={{ color: '#718096', margin: 0 }}>No students enrolled yet. Enroll a group or individual students to get started.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Course Modal */}
      {showDeleteModal && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.7)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}
          onClick={() => {
            setShowDeleteModal(false);
            setDeleteConfirmCode('');
          }}
        >
          <div 
            style={{ 
              backgroundColor: '#ffffff', 
              borderRadius: '12px', 
              padding: '24px', 
              width: '90%', 
              maxWidth: '500px' 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#991b1b' }}>
              ⚠️ Delete Course
            </h2>
            <p style={{ marginBottom: '16px', color: '#4a5568' }}>
              This action cannot be undone. This will permanently delete the course <strong>{course.title}</strong> and all its chapters, assets, and enrollment data.
            </p>
            <div className="form-group">
              <label>To confirm, type the course code: <strong>{course.code}</strong></label>
              <input
                type="text"
                value={deleteConfirmCode}
                onChange={(e) => setDeleteConfirmCode(e.target.value)}
                placeholder={course.code}
                style={{ 
                  background: '#ffffff', 
                  color: '#1a202c', 
                  border: '1px solid #e2e8f0',
                  textTransform: 'uppercase'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmCode('');
                }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn"
                style={{ 
                  background: '#991b1b',
                  color: '#ffffff'
                }}
                onClick={handleDeleteCourse}
                disabled={deleting || deleteConfirmCode.toUpperCase() !== course.code}
              >
                {deleting ? 'Deleting...' : 'Delete Course'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Course Modal */}
      {showCopyModal && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.7)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}
          onClick={() => {
            setShowCopyModal(false);
            setCopyForm({ newCode: '', newTitle: '', targetGroupIds: [] });
          }}
        >
          <div 
            style={{ 
              backgroundColor: '#ffffff', 
              borderRadius: '12px', 
              padding: '24px', 
              width: '90%', 
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#1a202c' }}>
              📋 Copy Course
            </h2>
            <form onSubmit={handleCopyCourse}>
              <div className="form-group">
                <label>New Course Code *</label>
                <input
                  type="text"
                  required
                  value={copyForm.newCode}
                  onChange={(e) => setCopyForm({ ...copyForm, newCode: e.target.value })}
                  placeholder="e.g., REACT-2024"
                  style={{ 
                    background: '#ffffff', 
                    color: '#1a202c', 
                    border: '1px solid #e2e8f0',
                    textTransform: 'uppercase'
                  }}
                />
              </div>

              <div className="form-group">
                <label>New Course Title (optional)</label>
                <input
                  type="text"
                  value={copyForm.newTitle}
                  onChange={(e) => setCopyForm({ ...copyForm, newTitle: e.target.value })}
                  placeholder={`${course.title} (Copy)`}
                  style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                />
              </div>

              <div className="form-group">
                <label>Enroll Groups (optional)</label>
                <Select
                  isMulti
                  menuPortalTarget={document.body}
                  options={availableGroups.map(group => ({ value: group._id, label: group.name }))}
                  onChange={(selected) => setCopyForm({ ...copyForm, targetGroupIds: selected ? selected.map(s => s.value) : [] })}
                  styles={{
                    control: (base) => ({ ...base, background: '#ffffff', borderColor: '#e2e8f0' }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
                <small style={{ color: '#718096', fontSize: '12px' }}>
                  Students from selected groups will be automatically enrolled in the new course
                </small>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowCopyModal(false);
                    setCopyForm({ newCode: '', newTitle: '', targetGroupIds: [] });
                  }}
                  disabled={copying}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={copying}
                >
                  {copying ? 'Copying...' : 'Copy Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enroll Group Modal */}
      {showEnrollGroupModal && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.7)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}
          onClick={() => {
            setShowEnrollGroupModal(false);
            setSelectedGroupId('');
          }}
        >
          <div 
            style={{ 
              backgroundColor: '#ffffff', 
              borderRadius: '12px', 
              padding: '24px', 
              width: '90%', 
              maxWidth: '500px' 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#1a202c' }}>
              Enroll Group
            </h2>
            <div className="form-group">
              <label>Select Group</label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
              >
                <option value="">-- Select Group --</option>
                {availableGroups
                  .filter(g => !enrollments.groups?.find(eg => eg._id?.toString() === g._id?.toString()))
                  .map(group => (
                    <option key={group._id} value={group._id}>
                      {group.name} ({group.students?.length || 0} students)
                    </option>
                  ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowEnrollGroupModal(false);
                  setSelectedGroupId('');
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleEnrollGroup}
              >
                Enroll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Students Modal */}
      {showEnrollStudentModal && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.7)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}
          onClick={() => {
            setShowEnrollStudentModal(false);
            setSelectedStudentIds([]);
          }}
        >
          <div 
            style={{ 
              backgroundColor: '#ffffff', 
              borderRadius: '12px', 
              padding: '24px', 
              width: '90%', 
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#1a202c' }}>
              Enroll Students
            </h2>
            <div className="form-group">
              <label>Select Students</label>
              <Select
                isMulti
                menuPortalTarget={document.body}
                options={availableStudents
                  .filter(s => !enrollments.students?.find(es => es._id === s._id))
                  .map(student => ({ 
                    value: student._id, 
                    label: `${student.name} (${student.email})` 
                  }))}
                onChange={(selected) => setSelectedStudentIds(selected ? selected.map(s => s.value) : [])}
                styles={{
                  control: (base) => ({ ...base, background: '#ffffff', borderColor: '#e2e8f0' }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowEnrollStudentModal(false);
                  setSelectedStudentIds([]);
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleEnrollStudents}
              >
                Enroll
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseSettings;
